import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useApp } from '../../contexts/AppContext';
import { DEPT_DB_MAP, DEPARTMENTS, formatAmount, formatAmountCompact, API, authFetch, titleCase } from '../../constants';
import StatusDot from '../../components/StatusDot';
import AuditTrail from '../../components/AuditTrail';
import { DetailSheet } from '../../components/finance/DetailSheet';
import StatusBadge from '../../components/ui/StatusBadge';
import { AiInsightCard } from '../../components/finance/AiInsightCard';
import { DetailSkeleton } from '../../components/finance/SkeletonStates';
import KpiGrid from '../../components/ui/KpiGrid';
import TabBar from '../../components/ui/TabBar';
import FilterBar from '../../components/ui/FilterBar';
import ActionButton from '../../components/ui/ActionButton';
import { isAutoResolved, needsReview, Pagination, WorkloadView } from './constants';

const WORKLOAD_TABS = [
  { key: 'all', label: 'All' },
  { key: 'needs_review', label: 'Needs Review' },
  { key: 'auto_resolved', label: 'Auto-Resolved' },
];

export default function ExceptionsTab() {
  const { filteredExceptions: allExceptions, loading, orgData } = useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [workloadView, setWorkloadView] = useState<WorkloadView>('all');
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || 'all');
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterDept, setFilterDept] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [expandedAudit, setExpandedAudit] = useState<any[]>([]);
  const [panelId, setPanelId] = useState<number | null>(null);
  const [panelData, setPanelData] = useState<any>(null);
  const [panelLoading, setPanelLoading] = useState(false);
  const pageSize = 50;

  // Workload counts (computed from full list before other filters)
  const needsReviewCount = allExceptions.filter(needsReview).length;
  const autoResolvedCount = allExceptions.filter(isAutoResolved).length;

  const exceptionTypes = Array.from(new Set(allExceptions.map((e: any) => e.exception_type).filter(Boolean)));
  const deptNames = Array.from(new Set(allExceptions.map((e: any) => DEPT_DB_MAP[e.department] || e.department).filter(Boolean)));

  // Apply workload view first, then other filters
  let filtered = allExceptions;
  if (workloadView === 'needs_review') filtered = filtered.filter(needsReview);
  else if (workloadView === 'auto_resolved') filtered = filtered.filter(isAutoResolved);

  if (filterStatus !== 'all') filtered = filtered.filter((e: any) => e.status?.toLowerCase() === filterStatus);
  if (filterSeverity !== 'all') filtered = filtered.filter((e: any) => e.severity?.toLowerCase() === filterSeverity);
  if (filterType !== 'all') filtered = filtered.filter((e: any) => e.exception_type === filterType);
  if (filterDept !== 'all') {
    filtered = filtered.filter((e: any) => (DEPT_DB_MAP[e.department] || e.department) === filterDept);
  }
  if (search) {
    const q = search.toLowerCase();
    filtered = filtered.filter((e: any) =>
      (e.title || '').toLowerCase().includes(q) ||
      (e.vendor_name || '').toLowerCase().includes(q) ||
      (e.exception_ref || '').toLowerCase().includes(q) ||
      String(e.id).includes(q)
    );
  }

  const totalPages = Math.ceil(filtered.length / pageSize);
  const paged = filtered.slice(page * pageSize, (page + 1) * pageSize);
  const totalExceptions = orgData?.total_exceptions || allExceptions.length;
  const stats = orgData?.processing_stats || {};

  const loadAudit = async (excId: number) => {
    if (expandedId === excId) { setExpandedId(null); return; }
    setExpandedId(excId);
    try {
      const res = await authFetch(`${API}/${excId}`);
      const data = await res.json();
      setExpandedAudit(data.audit_trail || []);
    } catch {
      setExpandedAudit([]);
    }
  };

  const openPanel = async (excId: number) => {
    setPanelId(excId);
    setPanelLoading(true);
    try {
      const res = await authFetch(`${API}/${excId}`);
      const data = await res.json();
      setPanelData(data);
    } catch {
      setPanelData(null);
    } finally {
      setPanelLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!panelId) return;
    try {
      await authFetch(`${API}/${panelId}/resolve`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'APPROVED', resolution_summary: 'Approved via panel' }),
      });
      openPanel(panelId);
    } catch {}
  };

  const handleEscalate = async () => {
    if (!panelId) return;
    try {
      await authFetch(`${API}/${panelId}/escalate`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: 'Escalated via panel' }),
      });
      openPanel(panelId);
    } catch {}
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 80, color: 'var(--text-secondary)' }}>Loading...</div>;

  return (
    <div>
      {/* KPI Strip */}
      <KpiGrid
        columns={5}
        items={[
          { label: 'Total', value: totalExceptions.toLocaleString(), color: 'var(--accent)' },
          { label: 'Needs Review', value: needsReviewCount.toLocaleString(), color: 'var(--amber)' },
          { label: 'Escalated', value: (stats.escalated || orgData?.status_breakdown?.escalated || 0).toLocaleString(), color: 'var(--red)' },
          { label: 'Auto-Resolved', value: autoResolvedCount.toLocaleString(), color: 'var(--green)' },
          { label: 'Est. Exposure', value: formatAmountCompact(orgData?.estimated_exposure || orgData?.amount_at_risk || 0), color: '#f59e0b' },
        ]}
      />

      {/* Workload View Toggle */}
      <TabBar
        tabs={WORKLOAD_TABS}
        active={workloadView}
        onChange={(key) => { setWorkloadView(key as WorkloadView); setPage(0); }}
      />

      {/* Filters */}
      <FilterBar
        filters={[
          { key: 'search', label: 'Search', type: 'search', value: search, onChange: (v) => { setSearch(v); setPage(0); }, placeholder: 'Search by title, vendor, ref, or ID...', width: 240 },
          { key: 'status', label: 'Status', type: 'select', value: filterStatus, onChange: (v) => { setFilterStatus(v); setPage(0); },
            options: [{ value: 'all', label: 'All Status' }, { value: 'detected', label: 'Detected' }, { value: 'classifying', label: 'Classifying' }, { value: 'classified', label: 'Classified' }, { value: 'reviewing', label: 'Reviewing' }, { value: 'approved', label: 'Approved' }, { value: 'rejected', label: 'Rejected' }, { value: 'escalated', label: 'Escalated' }, { value: 'closed', label: 'Closed' }] },
          { key: 'severity', label: 'Severity', type: 'select', value: filterSeverity, onChange: (v) => { setFilterSeverity(v); setPage(0); },
            options: [{ value: 'all', label: 'All Severity' }, { value: 'critical', label: 'Critical' }, { value: 'high', label: 'High' }, { value: 'medium', label: 'Medium' }, { value: 'low', label: 'Low' }] },
          { key: 'type', label: 'Type', type: 'select', value: filterType, onChange: (v) => { setFilterType(v); setPage(0); },
            options: [{ value: 'all', label: 'All Types' }, ...exceptionTypes.map(t => ({ value: t, label: titleCase(t) }))] },
          { key: 'dept', label: 'Department', type: 'select', value: filterDept, onChange: (v) => { setFilterDept(v); setPage(0); },
            options: [{ value: 'all', label: 'All Departments' }, ...deptNames.map(d => ({ value: d, label: d }))] },
        ]}
      />

      <div style={{ textAlign: 'right', fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 8, marginTop: -8 }}>
        {filtered.length} results
      </div>

      {/* Table header */}
      <div style={{ display: 'grid', gridTemplateColumns: '50px 1fr 100px 120px 70px 70px 120px', gap: 8, padding: '8px 14px', fontSize: 9, color: 'var(--text-tertiary)', letterSpacing: 1, textTransform: 'uppercase', fontWeight: 700 }}>
        <span>#</span><span>Exception</span><span>Amount</span><span>Department</span><span>Conf</span><span>Severity</span><span>Status</span>
      </div>

      {/* Exception rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {paged.length === 0 && (
          <div className="card" style={{ padding: 48, textAlign: 'center', color: 'var(--text-secondary)' }}>
            {allExceptions.length === 0 ? 'No exceptions yet. Upload GL data to get started.'
              : workloadView === 'needs_review' ? 'No exceptions need review. All caught up!'
              : workloadView === 'auto_resolved' ? 'No auto-resolved exceptions yet.'
              : 'No results match your filters.'}
          </div>
        )}
        {paged.map((exc: any) => {
          const mappedDept = DEPT_DB_MAP[exc.department] || exc.department;
          const deptColor = DEPARTMENTS[mappedDept]?.color || 'var(--text-tertiary)';
          const isExpanded = expandedId === exc.id;
          const autoRes = isAutoResolved(exc);
          return (
            <div key={exc.id}>
              <div
                onClick={() => openPanel(exc.id)}
                title="Click to open detail panel"
                style={{
                  display: 'grid', gridTemplateColumns: '50px 1fr 100px 120px 70px 70px 120px', gap: 8,
                  padding: '10px 14px',
                  background: isExpanded ? 'rgba(59,130,246,0.05)' : autoRes ? 'rgba(16,185,129,0.02)' : 'var(--bg-card)',
                  border: isExpanded ? '1px solid rgba(59,130,246,0.2)' : autoRes ? '1px solid rgba(16,185,129,0.12)' : '1px solid var(--border-default)',
                  borderRadius: 8, cursor: 'pointer', fontSize: 11, alignItems: 'center',
                }}
              >
                <span className="mono" style={{ color: 'var(--text-tertiary)', fontSize: 10 }}>{exc.id}</span>
                <span style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {exc.title || titleCase(exc.exception_type) || 'Exception'}
                </span>
                <span className="mono" style={{ fontWeight: 700 }}>{formatAmount(exc.amount || 0)}</span>
                <span style={{ color: deptColor, fontSize: 10 }}>{mappedDept}</span>
                <span className="mono" style={{ fontWeight: 700, color: (exc.confidence || 0) >= 0.85 ? 'var(--green)' : (exc.confidence || 0) >= 0.6 ? 'var(--amber)' : 'var(--text-tertiary)' }}>
                  {exc.confidence ? `${(exc.confidence * 100).toFixed(0)}%` : 'N/A'}
                </span>
                <span style={{ fontSize: 9, textTransform: 'uppercase', color: exc.severity === 'critical' ? 'var(--red)' : exc.severity === 'high' ? 'var(--amber)' : 'var(--text-secondary)' }}>
                  {exc.severity || '-'}
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  {exc.source_workflow_instance_id && (
                    <span style={{ fontSize: 8, padding: '1px 5px', background: 'rgba(139,92,246,0.12)', color: 'var(--navy-800)', borderRadius: 3, fontWeight: 700, whiteSpace: 'nowrap' }}>
                      WF
                    </span>
                  )}
                  {autoRes ? (
                    <span style={{ fontSize: 9, padding: '2px 7px', background: 'rgba(16,185,129,0.12)', color: 'var(--green)', borderRadius: 4, fontWeight: 700, whiteSpace: 'nowrap' }}>
                      Auto-Resolved &#10003;
                    </span>
                  ) : (
                    <>
                      <StatusDot status={exc.status} size={6} />
                      <span style={{ fontSize: 9, whiteSpace: 'nowrap' }}>{titleCase(exc.status)}</span>
                    </>
                  )}
                </div>
              </div>
              {isExpanded && expandedAudit.length > 0 && (
                <div style={{ padding: '12px 14px 12px 64px' }}>
                  <AuditTrail entries={expandedAudit} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && <Pagination page={page} totalPages={totalPages} onPage={setPage} />}

      {/* Exception Detail Side Panel */}
      <DetailSheet
        open={panelId !== null}
        onClose={() => { setPanelId(null); setPanelData(null); }}
        title={panelData ? `EXC-${panelId}` : 'Loading...'}
        subtitle={titleCase(panelData?.exception_type)}
        statusBadge={panelData?.status ? <StatusBadge status={panelData.status.toUpperCase()} /> : undefined}
        footer={
          panelData && !['approved', 'resolved_auto', 'resolved_human', 'closed'].includes((panelData.status || '').toLowerCase()) ? (
            <div style={{ display: 'flex', gap: 8 }}>
              <ActionButton label="Approve" onClick={handleApprove} variant="primary" size="sm" />
              <ActionButton label="Escalate" onClick={handleEscalate} variant="secondary" size="sm" />
              <div style={{ marginLeft: 'auto' }}>
                <ActionButton label="Full Detail &rarr;" onClick={() => navigate(`/activity-tracker/${panelId}`)} variant="ghost" size="sm" />
              </div>
            </div>
          ) : undefined
        }
      >
        {panelLoading ? (
          <DetailSkeleton />
        ) : panelData ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Metric strip */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
              {[
                { label: 'Amount', value: formatAmount(panelData.amount || 0) },
                { label: 'Entity', value: panelData.entity_code || '\u2014' },
                { label: 'Department', value: DEPT_DB_MAP[panelData.department] || panelData.department || '\u2014' },
                { label: 'Severity', value: panelData.severity || '\u2014' },
              ].map((m, i) => (
                <div key={i} style={{ padding: 10, background: 'var(--bg-card)', borderRadius: 6, border: '1px solid var(--border-subtle)' }}>
                  <div style={{ fontSize: 9, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.8 }}>{m.label}</div>
                  <div className="mono" style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginTop: 2 }}>{m.value}</div>
                </div>
              ))}
            </div>

            {/* Title */}
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 2 }}>Title</div>
              <div style={{ fontSize: 13, color: 'var(--text-primary)' }}>{panelData.title || 'No title'}</div>
            </div>

            {/* Description */}
            {panelData.description && (
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 2 }}>Description</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{panelData.description}</div>
              </div>
            )}

            {/* AI Resolution */}
            {panelData.resolution_mode && (
              <AiInsightCard
                title={panelData.resolution_mode === 'MODE_1' ? 'Mode 1 \u2014 Template Resolution' : 'Mode 2 \u2014 AI Resolution'}
                confidence={panelData.confidence ? Math.round(panelData.confidence * 100) : 0}
                description={panelData.resolution_summary || panelData.ai_narrative || 'Resolution analysis pending.'}
                citation={(() => {
                  // LLM may return either string[] OR {standard, section, citation}[].
                  // Coerce the first element to a display string so AiInsightCard
                  // (which renders citation directly) doesn't React-#31-crash.
                  const s: any = panelData.standards_applied?.[0];
                  if (s === undefined || s === null) return undefined;
                  return typeof s === 'string'
                    ? s
                    : (s.standard ?? s.section ?? s.citation ?? JSON.stringify(s));
                })()}
                journalEntry={
                  panelData.journal_entries?.slice(0, 4).map((je: any) => ({
                    account_code: je.account_code,
                    account_name: je.account_name || '',
                    debit: je.debit_amount || 0,
                    credit: je.credit_amount || 0,
                  })) || undefined
                }
              />
            )}

            {/* Audit Trail */}
            {panelData.audit_trail && panelData.audit_trail.length > 0 && (
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 6 }}>Audit Trail</div>
                <AuditTrail entries={panelData.audit_trail} />
              </div>
            )}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>
            Failed to load exception details
          </div>
        )}
      </DetailSheet>
    </div>
  );
}
